import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Tooltip, Space } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { getNoteIcon } from '../../utils/noteIcon';
import { useTranslation } from 'react-i18next';
import ChatPanel from './ChatPanel';
import QuizPanel from './QuizPanel';
import BrainstormPanel from './BrainstormPanel';
import TiptapEditor from '../TiptapEditor/TiptapEditor';
import { handleSourceUrl } from '../../pages/DashboardUtils';
import {
  getSelectedFolder,
  getSelectedTopic,
  getSelectedKB,
  getSelectedTag,
  getSearchKeyword
} from '../../hooks/useDashboardGlobalVar';

/**
 * 卡片内容全屏显示组件
 * 双列布局：左侧显示笔记内容，右侧显示AI工具面板
 */
const CardContent = ({
  selectedNote,
  realContent,
  isLoadingContent,
  onNoteClick,
  onQATopicClick,
  onTabChange,
  currentFilterState,
  isEditing,
  htmlContent,
  isSaving,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onContentChange,
  initialTool
}) => {
  const { t } = useTranslation(['dashboard', 'common', 'messages']);

  // 分割线位置（左侧宽度百分比，默认60%）
  const [leftWidth, setLeftWidth] = useState(65);

  // 拖拽相关状态
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const dragStartX = useRef(0);
  const dragStartLeftWidth = useRef(65);

  // Active tool state: 'chat' | 'quiz' | 'brainstorm'
  const [activeTool, setActiveTool] = useState(initialTool || 'chat');

  // Sync activeTool when initialTool changes
  useEffect(() => {
    if (initialTool) {
      setActiveTool(initialTool);
    }
  }, [initialTool]);

  // 处理分割线拖拽开始
  const handleDragStart = useCallback((e) => {
    setIsDragging(true);
    dragStartX.current = e.clientX;
    dragStartLeftWidth.current = leftWidth;
    e.preventDefault();
  }, [leftWidth]);

  // 处理分割线拖拽
  const handleDrag = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    const deltaX = e.clientX - dragStartX.current;
    const deltaPercent = (deltaX / containerWidth) * 100;

    let newLeftWidth = dragStartLeftWidth.current + deltaPercent;

    // 限制左侧宽度在 30% 到 80% 之间
    newLeftWidth = Math.max(30, Math.min(80, newLeftWidth));

    setLeftWidth(newLeftWidth);
  }, [isDragging]);

  // 处理分割线拖拽结束
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 监听全局鼠标事件
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDrag);
      document.addEventListener('mouseup', handleDragEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleDrag);
        document.removeEventListener('mouseup', handleDragEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleDrag, handleDragEnd]);

  // 获取当前筛选状态（如果没有传入，从全局状态获取）
  const filterState = currentFilterState || {
    selectedFolder: getSelectedFolder(),
    selectedTopic: getSelectedTopic(),
    selectedKB: getSelectedKB(),
    selectedTag: getSelectedTag(),
    searchKeyword: getSearchKeyword()
  };

  return (
    <div 
      ref={containerRef}
      style={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'row',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* 左侧内容区域 */}
      <div
        style={{
          width: `${leftWidth}%`,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: isDragging ? 'none' : 'width 0.3s ease',
          backgroundColor: '#fafafa'
        }}
      >
        {/* 标题栏 - fixed at top */}
        <div style={{
          padding: '24px 32px 0',
          borderBottom: '1px solid #e5e7eb',
          paddingBottom: 16,
          backgroundColor: '#fafafa',
          flexShrink: 0
        }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div style={{ flex: 1 }}>
                <h1 style={{
                  margin: 0,
                  marginBottom: 8,
                  fontSize: '24px',
                  fontWeight: 500,
                  lineHeight: 1.25,
                  color: '#111827',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  {getNoteIcon(selectedNote, { fontSize: '24px', marginRight: '10px' })}
                  {selectedNote?.title || t('dashboard:no_title', { defaultValue: 'No title' })}
                </h1>

                {(() => {
                  const sourceUrlInfo = handleSourceUrl(selectedNote?.source_url);
                  if (!sourceUrlInfo) {
                    return null;
                  }

                  const getDisplayText = (url) => {
                    let displayText = '';
                    if (url.startsWith('S3://')) {
                      const parts = url.replace('S3://', '').split('/');
                      displayText = parts[parts.length - 1] || t('dashboard:file', { defaultValue: 'File' });
                    } else {
                      displayText = url;
                    }
                    const maxLength = 60;
                    if (displayText.length > maxLength) {
                      return displayText.substring(0, maxLength) + '...';
                    }
                    return displayText;
                  };

                  return (
                    <a
                      href={sourceUrlInfo.isS3File ? "#" : sourceUrlInfo.url}
                      target={sourceUrlInfo.isS3File ? "_self" : "_blank"}
                      rel="noopener noreferrer"
                      onClick={sourceUrlInfo.onClick}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: '14px',
                        color: '#2563eb',
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#1d4ed8';
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#2563eb';
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>🔗</span>
                      <span>{getDisplayText(sourceUrlInfo.url)}</span>
                    </a>
                  );
                })()}
              </div>

              <Space size={8}>
                {isEditing ? (
                  <>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={onSaveEdit}
                      loading={isSaving}
                      size="small"
                    >
                      {t('common:save', { defaultValue: 'Save' })}
                    </Button>
                    <Button
                      icon={<CloseOutlined />}
                      onClick={onCancelEdit}
                      disabled={isSaving}
                      size="small"
                    >
                      {t('common:cancel', { defaultValue: 'Cancel' })}
                    </Button>
                  </>
                ) : (
                  <Tooltip
                    title={<span style={{ color: '#000' }}>{t('dashboard:edit_content', { defaultValue: 'Edit Content' })}</span>}
                    color="#fff"
                  >
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={onStartEdit}
                      style={{
                        borderRadius: '6px',
                        color: '#2563eb'
                      }}
                    />
                  </Tooltip>
                )}
              </Space>
            </div>
          </div>

          {/* 内容区域 - full width for interactive academic layout */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {isLoadingContent ? (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
                {t('dashboard:content_loading', { defaultValue: 'Content loading...' })}
              </div>
            ) : isEditing ? (
              <div style={{ flex: 1, overflow: 'hidden', padding: '0 32px 24px', display: 'flex', flexDirection: 'column' }}>
                <TiptapEditor
                  value={htmlContent}
                  onChange={onContentChange}
                  style={{ border: 'none', flex: 1, minHeight: 0 }}
                  placeholder={t('dashboard:edit_content_placeholder', { defaultValue: 'Start writing...' })}
                />
              </div>
            ) : (
              <div
                className="content-html-wrapper"
                dangerouslySetInnerHTML={{
                  __html: realContent || t('dashboard:no_content', { defaultValue: 'No content' })
                }}
              />
            )}
          </div>
      </div>

      {/* 可拖拽分割线 */}
        <div
          onMouseDown={handleDragStart}
          style={{
            width: '8px',
            height: '100%',
            backgroundColor: isDragging ? '#e6f7ff' : '#f5f5f5',
            cursor: 'col-resize',
            flexShrink: 0,
            position: 'relative',
            transition: isDragging ? 'none' : 'all 0.3s ease',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => {
            if (!isDragging) {
              e.target.style.backgroundColor = '#e6f7ff';
              e.target.style.boxShadow = '0 0 8px rgba(24, 144, 255, 0.2)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isDragging) {
              e.target.style.backgroundColor = '#f5f5f5';
              e.target.style.boxShadow = 'none';
            }
          }}
        >
          <div style={{
            width: '2px',
            height: '40px',
            backgroundColor: isDragging ? '#1890ff' : '#d9d9d9',
            borderRadius: '1px',
            transition: isDragging ? 'none' : 'background-color 0.3s ease'
          }} />
        </div>

      {/* 右侧AI工具区域 */}
        <div
          style={{
            width: `${100 - leftWidth}%`,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: isDragging ? 'none' : 'width 0.3s ease',
            backgroundColor: '#fafafa'
          }}
        >
          {activeTool === 'chat' && (
            <ChatPanel
              selectedNote={selectedNote}
              currentFilterState={filterState}
            />
          )}
          {activeTool === 'quiz' && (
            <QuizPanel
              selectedNote={selectedNote}
            />
          )}
          {activeTool === 'brainstorm' && (
            <BrainstormPanel
              selectedNote={selectedNote}
            />
          )}
        </div>
    </div>
  );
};

export default CardContent;

